import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash, X, Copy, Wallet, Repeat, Play, MagnifyingGlass, Lightning, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useCurrency } from "../../lib/useCurrency";
import { Th, Td, LocationChip } from "./TrainerSchedule";

export default function TrainerClients() {
  const navigate = useNavigate();
  const { symbol } = useCurrency();
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState(() => {
    try { return window.localStorage.getItem("traina.clientsLocationFilter") || "all"; } catch { return "all"; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("traina.clientsLocationFilter", locationFilter); } catch { /* noop */ }
  }, [locationFilter]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [planFor, setPlanFor] = useState(null);
  const [spFor, setSpFor] = useState(null);
  const [runningBilling, setRunningBilling] = useState(false);

  const load = async () => {
    try { setClients((await api.get("/clients")).data); } catch (_e) {}
    try { setLocations((await api.get("/locations")).data || []); } catch (_e) {}
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this client and all their data?")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const runBilling = async () => {
    if (!window.confirm("Generate monthly invoices for all clients with an active plan? (Idempotent — skips clients already billed this month.)")) return;
    setRunningBilling(true);
    try {
      const { data } = await api.post("/billing/run-monthly");
      toast.success(`Billing run complete — ${data.created.length} invoiced, ${data.skipped_count} skipped`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRunningBilling(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = clients;
    if (locationFilter === "__none") rows = rows.filter((c) => !c.default_location);
    else if (locationFilter !== "all") rows = rows.filter((c) => c.default_location === locationFilter);
    if (!q) return rows;
    return rows.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q)
    );
  }, [clients, search, locationFilter]);

  return (
    <div className="space-y-8" data-testid="trainer-clients">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Clients ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Clients.</h1>
          <p className="text-zinc-500 text-xs mt-2">Monthly billing auto-runs on the 1st of each month. Use "Run billing now" to test.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={runBilling} disabled={runningBilling} className="btn-ghost" data-testid="run-billing-btn">
            <Play size={14} weight="fill" /> {runningBilling ? "Running…" : "Run billing now"}
          </button>
          <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="open-client-form">
            <Plus size={16} weight="bold" /> Add client
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap" data-testid="client-search-bar">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or phone…"
            className="w-full pl-9 pr-9 py-2.5 border border-white/10 bg-[#0F0F0F] focus:border-brand outline-none text-sm"
            data-testid="client-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              aria-label="Clear search"
              data-testid="client-search-clear"
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
        <div className="tracker text-zinc-500" data-testid="client-search-count">
          {search || locationFilter !== "all" ? `${filtered.length} of ${clients.length}` : `${clients.length} total`}
        </div>
      </div>

      {(locations.length > 0 || clients.some((c) => c.default_location)) && (
        <ClientLocationFilterChips
          locations={locations}
          clients={clients}
          value={locationFilter}
          onChange={setLocationFilter}
        />
      )}

      <div className="card-flat overflow-x-auto">
        {clients.length === 0 ? (
          <div className="p-16 text-center text-zinc-500 tracker">No clients yet — add your first one</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-zinc-500 tracker">No clients match "{search}"</div>
        ) : (
          <table className="w-full" data-testid="clients-table">
            <thead>
              <tr className="border-b border-white/10">
                <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Pot</Th><Th>Monthly Plan</Th><Th>Joined</Th><Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const bal = Number(c.balance || 0);
                const plan = c.monthly_plan;
                const planActive = plan && plan.active;
                return (
                  <tr
                    key={c.id}
                    className="border-b border-white/5 hover:bg-[#1A1A1A] cursor-pointer"
                    onClick={() => navigate(`/trainer/clients/${c.id}`)}
                    data-testid={`client-row-${c.id}`}
                  >
                    <Td>
                      <Link
                        to={`/trainer/clients/${c.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-semibold hover:underline decoration-brand underline-offset-4"
                        data-testid={`client-name-link-${c.id}`}
                      >
                        {c.name}
                      </Link>
                      {c.default_location && (
                        <div
                          className="tracker text-[9px] text-brand-light mt-0.5 inline-flex items-center gap-0.5"
                          data-testid={`client-default-loc-${c.id}`}
                          title={`Default location: ${c.default_location}`}
                        >
                          <MapPin size={9} weight="bold" /> {c.default_location}
                        </div>
                      )}
                    </Td>
                    <Td><span className="font-mono text-sm">{c.email}</span></Td>
                    <Td><span className="font-mono text-sm text-zinc-400">{c.phone || "—"}</span></Td>
                    <Td>
                      <span className={`font-mono font-semibold money ${bal < 0 ? "text-[#FF6B61]" : bal === 0 ? "text-zinc-500" : "text-emerald-400"}`}>
                        {symbol}{bal.toFixed(2)}
                      </span>
                    </Td>
                    <Td>
                      {c.session_plan ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="tracker px-2 py-1 border border-brand/40 text-brand-light inline-flex items-center gap-1 w-fit" data-testid={`sp-badge-${c.id}`}>
                            <Lightning size={12} weight="bold" /> {c.sessions_remaining ?? "?"} / {(c.session_plan.sessions_per_week || 0) * 4} left
                          </span>
                          {c.session_plan.end_date && (
                            <span className="tracker text-[10px] text-zinc-500" data-testid={`sp-end-date-${c.id}`}>
                              ends {c.session_plan.end_date}
                            </span>
                          )}
                        </div>
                      ) : planActive ? (
                        <span className="tracker px-2 py-1 border border-brand/40 text-brand-light inline-flex items-center gap-1">
                          <Repeat size={12} weight="bold" /> <span className="money">{symbol}{plan.amount.toFixed(0)}/mo</span>
                        </span>
                      ) : plan ? (
                        <span className="tracker text-zinc-500">paused</span>
                      ) : (
                        <span className="tracker text-zinc-600">—</span>
                      )}
                    </Td>
                    <Td><span className="tracker">{new Date(c.created_at).toLocaleDateString()}</span></Td>
                    <Td align="right">
                      <div className="flex items-center gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setSpFor(c)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`set-sp-${c.id}`} title="Session plan">
                          <Lightning size={14} weight="bold" />
                        </button>
                        <button onClick={() => setPlanFor(c)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`set-plan-${c.id}`} title="Monthly plan">
                          <Repeat size={14} weight="bold" />
                        </button>
                        <button onClick={() => setAdjustFor(c)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`adjust-credit-${c.id}`} title="Adjust pot">
                          <Wallet size={14} weight="bold" />
                        </button>
                        <button onClick={() => remove(c.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`delete-client-${c.id}`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <ClientForm
          onClose={() => setShowForm(false)}
          onCreated={(info) => { load(); setCreatedInfo(info); }}
        />
      )}

      {adjustFor && (
        <AdjustCreditModal client={adjustFor} onClose={() => setAdjustFor(null)} onDone={load} />
      )}

      {planFor && (
        <MonthlyPlanModal client={planFor} onClose={() => setPlanFor(null)} onDone={load} />
      )}

      {spFor && (
        <SessionPlanModal client={spFor} onClose={() => setSpFor(null)} onDone={load} />
      )}

      {createdInfo && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setCreatedInfo(null)}>
          <div className="card-flat w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="client-created-modal">
            <div className="tracker text-brand mb-2">[ Client created ]</div>
            <h2 className="font-display text-3xl mb-4">Share login.</h2>
            <p className="text-zinc-400 text-sm mb-6">Send these credentials to your client so they can access their portal.</p>
            <div className="card-flat p-4 space-y-3 mb-6">
              <KV k="Email" v={createdInfo.email} />
              <KV k="Password" v={createdInfo.temp_password} />
            </div>
            <button onClick={() => setCreatedInfo(null)} className="btn-primary w-full justify-center">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MonthlyPlanModal({ client, onClose, onDone }) {
  const { symbol } = useCurrency();
  const initial = client.monthly_plan || { amount: 600, description: "Monthly training package", active: true, notify_channels: ["email"] };
  const isNewPlan = !client.monthly_plan;
  const [form, setForm] = useState({
    amount: Number(initial.amount) || 0,
    description: initial.description || "Monthly training package",
    active: !!initial.active,
    notify_email: (initial.notify_channels || []).includes("email"),
    prorate_first_month: isNewPlan, // default on for new plans, off for edits
  });
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Compute prorated preview
  const now = new Date();
  const day = now.getUTCDate();
  const month = now.getUTCMonth();
  const year = now.getUTCFullYear();
  const totalDays = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const daysRemaining = totalDays - day + 1;
  const proratedAmount = day > 1 ? Math.round(((Number(form.amount) || 0) * daysRemaining) / totalDays * 100) / 100 : null;

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const channels = form.notify_email ? ["email"] : ["email"];
      const { data } = await api.post(`/clients/${client.id}/monthly-plan`, {
        amount: Number(form.amount),
        description: form.description,
        active: form.active,
        notify_channels: channels,
        prorate_first_month: form.prorate_first_month,
      });
      if (data.prorated_invoice) {
        toast.success(`Plan saved · prorated invoice ${symbol}${data.prorated_invoice.amount.toFixed(2)} created`);
      } else {
        toast.success("Monthly plan saved");
      }
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const removePlan = async () => {
    if (!window.confirm("Remove this client's monthly plan?")) return;
    setRemoving(true);
    try {
      await api.delete(`/clients/${client.id}/monthly-plan`);
      toast.success("Plan removed");
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-flat w-full max-w-md p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="monthly-plan-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ Monthly plan ]</div>
            <h2 className="font-display text-3xl">{client.name}</h2>
            <p className="text-zinc-500 text-xs mt-1">Auto-invoiced on the 1st of every month.</p>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>

        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Amount per month ($)</label>
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full px-4 py-3 border"
              data-testid="plan-amount-input"
            />
          </div>
          <div>
            <label className="tracker block mb-2">Description</label>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-3 border"
              data-testid="plan-desc-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "4 sessions", amount: 320 },
              { label: "8 sessions", amount: 600 },
              { label: "12 sessions", amount: 840 },
              { label: "16 sessions", amount: 1080 },
            ].map((p) => (
              <button
                key={p.amount}
                type="button"
                onClick={() => setForm((f) => ({ ...f, amount: p.amount, description: `${p.label}/month` }))}
                className={`p-3 border text-left ${Number(form.amount) === p.amount ? "border-brand bg-brand/10" : "border-white/10 hover:border-white/40"}`}
                data-testid={`plan-preset-${p.amount}`}
              >
                <div className="font-display text-base leading-none mb-1">{p.label}</div>
                <div className="tracker text-zinc-500"><span className="money">{symbol}{p.amount}/mo</span></div>
              </button>
            ))}
          </div>

          <div className="card-flat p-4 space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="tracker">Active (auto-bill on 1st)</span>
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="plan-active-toggle" />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="tracker">Email notification</span>
              <input type="checkbox" checked={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.checked })} data-testid="plan-notify-email" />
            </label>
            {isNewPlan && day > 1 && (
              <label className="flex items-center justify-between cursor-pointer pt-3 border-t border-white/5">
                <div>
                  <div className="tracker">Prorate first month</div>
                  <div className="text-[10px] text-zinc-500 mt-1">Charge a proportional amount immediately for {daysRemaining} of {totalDays} days</div>
                </div>
                <input type="checkbox" checked={form.prorate_first_month} onChange={(e) => setForm({ ...form, prorate_first_month: e.target.checked })} data-testid="plan-prorate-toggle" />
              </label>
            )}
          </div>

          {isNewPlan && form.prorate_first_month && proratedAmount !== null && Number(form.amount) > 0 && (
            <div className="card-flat p-4 border-brand/40 bg-brand/5" data-testid="prorate-preview">
              <div className="flex items-center justify-between mb-1">
                <div className="tracker text-brand">First invoice (today)</div>
                <div className="font-display text-2xl money">{symbol}{proratedAmount.toFixed(2)}</div>
              </div>
              <div className="text-[10px] text-zinc-400">
                <span className="money">{symbol}{Number(form.amount).toFixed(2)}</span> × {daysRemaining}/{totalDays} days remaining
              </div>
            </div>
          )}

          <div className="flex gap-2">
            {client.monthly_plan && (
              <button type="button" onClick={removePlan} disabled={removing} className="btn-ghost flex-1 justify-center" data-testid="plan-remove">
                {removing ? "Removing…" : "Remove"}
              </button>
            )}
            <button type="submit" disabled={loading} className="btn-primary flex-1 justify-center" data-testid="plan-submit">
              {loading ? "Saving…" : "Save plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdjustCreditModal({ client, onClose, onDone }) {
  const { symbol } = useCurrency();
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("Monthly package");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt) return toast.error("Enter a non-zero amount");
    setLoading(true);
    try {
      await api.post(`/clients/${client.id}/credits/adjust`, { amount: amt, description: desc });
      toast.success(`Pot adjusted by ${symbol}${amt.toFixed(2)}`);
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const bal = Number(client.balance || 0);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-flat w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="adjust-credit-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ Adjust pot ]</div>
            <h2 className="font-display text-3xl">{client.name}</h2>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>

        <div className="card-flat p-4 mb-5">
          <div className="tracker mb-1">Current balance</div>
          <div className="font-display text-3xl money">{symbol}{bal.toFixed(2)}</div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Amount (+ to add, − to deduct)</label>
            <input
              required
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 500 for monthly package"
              className="w-full px-4 py-3 border"
              data-testid="adjust-amount-input"
            />
          </div>
          <div>
            <label className="tracker block mb-2">Description</label>
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="w-full px-4 py-3 border"
              data-testid="adjust-desc-input"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[250, 500, 1000].map((amt) => (
              <button key={amt} type="button" onClick={() => setAmount(String(amt))} className="btn-ghost !py-2 !text-[11px]" data-testid={`quick-${amt}`}>
                +${amt}
              </button>
            ))}
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="adjust-submit">
            {loading ? "Applying…" : "Apply adjustment"}
          </button>
        </form>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  const copy = () => { navigator.clipboard.writeText(v); toast.success("Copied"); };
  return (
    <div className="flex items-center justify-between">
      <div className="tracker">{k}</div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm">{v}</span>
        <button onClick={copy} className="text-zinc-500 hover:text-white"><Copy size={14} weight="bold" /></button>
      </div>
    </div>
  );
}

function ClientForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", default_location: "" });
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/locations");
        if (alive) setLocations(data || []);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/clients", form);
      toast.success("Client added");
      onCreated(data);
      onClose();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 403 && (detail || "").includes("Client limit reached")) {
        toast.error(detail, {
          action: { label: "Upgrade plan", onClick: () => { window.location.href = "/trainer/settings?upgrade=1"; } },
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-flat w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="client-form-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ New client ]</div>
            <h2 className="font-display text-3xl">Add.</h2>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Name</label>
            <input required value={form.name} onChange={set("name")} className="w-full px-4 py-3 border" data-testid="client-name" />
          </div>
          <div>
            <label className="tracker block mb-2">Email</label>
            <input required type="email" value={form.email} onChange={set("email")} className="w-full px-4 py-3 border" data-testid="client-email" />
          </div>
          <div>
            <label className="tracker block mb-2">Phone</label>
            <input value={form.phone} onChange={set("phone")} placeholder="+1 555 555 0123" className="w-full px-4 py-3 border" data-testid="client-phone" />
          </div>
          <div>
            <label className="tracker block mb-2">Default location (optional)</label>
            <select value={form.default_location} onChange={set("default_location")} className="w-full px-4 py-3 border bg-[#0A0A0A]" data-testid="client-default-location">
              <option value="">— None (pick per session) —</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.name}>{loc.name}</option>
              ))}
            </select>
            <div className="tracker text-[9px] text-zinc-600 mt-1">Pre-fills every new booking for this client. Editable later on their profile.</div>
          </div>
          <div>
            <label className="tracker block mb-2">Password (auto-generated if blank)</label>
            <input value={form.password} onChange={set("password")} placeholder="leave blank to generate" className="w-full px-4 py-3 border" data-testid="client-password" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="client-submit">
            {loading ? "Adding…" : "Add client"}
          </button>
        </form>
      </div>
    </div>
  );
}

function SessionPlanModal({ client, onClose, onDone }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    sessions_per_week: client.session_plan?.sessions_per_week || 3,
    monthly_rate: client.session_plan?.monthly_rate || 400,
    currency: client.session_plan?.currency || "gbp",
    end_date: client.session_plan?.end_date || "",
  });
  const [adjust, setAdjust] = useState({ delta: 0, reason: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${client.id}/session-plan`);
      setState(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}/session-plan`, form);
      toast.success("Session plan saved");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const detach = async () => {
    if (!window.confirm("Remove the session plan? Client will revert to legacy pot billing — existing rollover credit will be wiped.")) return;
    setSaving(true);
    try {
      await api.delete(`/clients/${client.id}/session-plan`);
      toast.success("Plan removed");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const applyAdjust = async () => {
    if (!adjust.delta) return;
    setSaving(true);
    try {
      await api.post(`/clients/${client.id}/session-plan/adjust`, {
        delta: Number(adjust.delta),
        reason: adjust.reason || "manual_adjustment",
      });
      toast.success("Adjusted");
      setAdjust({ delta: 0, reason: "" });
      load();
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const monthlyCount = (Number(form.sessions_per_week) || 0) * 4;
  const perSession = monthlyCount > 0 ? Number(form.monthly_rate) / monthlyCount : 0;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="card-flat p-8 w-full max-w-lg my-10" onClick={(e) => e.stopPropagation()} data-testid="session-plan-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="tracker text-brand mb-1">[ Session plan ]</div>
            <h2 className="font-display text-3xl">{client.name}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost !p-2"><X size={14} weight="bold" /></button>
        </div>

        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : (
          <>
            <form onSubmit={save} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <div className="tracker text-zinc-500 text-[10px] mb-1">SESSIONS / WEEK</div>
                  <input
                    type="number"
                    min="1"
                    max="14"
                    value={form.sessions_per_week}
                    onChange={(e) => setForm({ ...form, sessions_per_week: Number(e.target.value) })}
                    className="w-full px-3 py-2 border bg-[#0A0A0A]"
                    data-testid="sp-sessions-per-week"
                    required
                  />
                </label>
                <label className="block">
                  <div className="tracker text-zinc-500 text-[10px] mb-1">MONTHLY RATE</div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.monthly_rate}
                    onChange={(e) => setForm({ ...form, monthly_rate: Number(e.target.value) })}
                    className="w-full px-3 py-2 border bg-[#0A0A0A]"
                    data-testid="sp-monthly-rate"
                    required
                  />
                </label>
              </div>
              <label className="block">
                <div className="tracker text-zinc-500 text-[10px] mb-1">CURRENCY</div>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="w-full px-3 py-2 border bg-[#0A0A0A]"
                  data-testid="sp-currency"
                >
                  <option value="gbp">GBP (£)</option>
                  <option value="usd">USD ($)</option>
                  <option value="eur">EUR (€)</option>
                </select>
              </label>
              <label className="block">
                <div className="tracker text-zinc-500 text-[10px] mb-1">END DATE (OPTIONAL)</div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={form.end_date || ""}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="flex-1 px-3 py-2 border bg-[#0A0A0A]"
                    data-testid="sp-end-date"
                  />
                  {form.end_date && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, end_date: "" })}
                      className="btn-ghost !px-3 text-xs"
                      data-testid="sp-end-date-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">Leave blank for an ongoing plan.</div>
              </label>
              <div className="card-flat p-3 bg-brand/5 border-brand/30 text-xs text-zinc-400 grid grid-cols-2 gap-2">
                <div><span className="tracker text-zinc-500 block">Monthly count</span><span className="font-mono text-zinc-300">{monthlyCount}</span></div>
                <div><span className="tracker text-zinc-500 block">Per session</span><span className="font-mono text-zinc-300">{perSession.toFixed(2)}</span></div>
              </div>
              <div className="flex gap-3 justify-end">
                {state?.on_plan && (
                  <button type="button" onClick={detach} disabled={saving} className="btn-ghost text-red-400" data-testid="sp-detach">
                    Remove plan
                  </button>
                )}
                <button type="submit" disabled={saving} className="btn-primary" data-testid="sp-save">
                  {saving ? "Saving…" : state?.on_plan ? "Update plan" : "Activate plan"}
                </button>
              </div>
            </form>

            {state?.on_plan && (
              <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
                <div className="tracker text-zinc-500 mb-2">[ Manual session-count adjust ]</div>
                <div className="text-xs text-zinc-400 grid grid-cols-2 gap-2 mb-3">
                  <div><span className="tracker text-zinc-500 block">Remaining</span><span className="font-mono text-zinc-200">{state.sessions_remaining}</span></div>
                  <div><span className="tracker text-zinc-500 block">Pending credit</span><span className="font-mono text-emerald-400">{Number(state.session_credits_pending || 0).toFixed(2)}</span></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="number"
                    value={adjust.delta}
                    onChange={(e) => setAdjust({ ...adjust, delta: e.target.value })}
                    placeholder="+/-"
                    className="px-3 py-2 border bg-[#0A0A0A]"
                    data-testid="sp-adjust-delta"
                  />
                  <input
                    type="text"
                    value={adjust.reason}
                    onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })}
                    placeholder="reason (optional)"
                    className="px-3 py-2 border bg-[#0A0A0A] col-span-2"
                    data-testid="sp-adjust-reason"
                  />
                </div>
                <button onClick={applyAdjust} disabled={saving || !adjust.delta} className="btn-ghost w-full justify-center" data-testid="sp-adjust-btn">
                  Apply
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ClientLocationFilterChips({ locations, clients, value, onChange }) {
  // Only render when there's actually something to filter — either the trainer
  // has locations configured, or at least one client already has a default_location.
  const usedLocations = useMemo(() => {
    const set = new Set();
    (clients || []).forEach((c) => { if (c.default_location) set.add(c.default_location); });
    return set;
  }, [clients]);
  const chips = useMemo(() => {
    const seen = new Set();
    const list = [];
    (locations || []).forEach((l) => {
      if (l.active !== false && !seen.has(l.name)) { list.push({ id: l.id, name: l.name }); seen.add(l.name); }
    });
    // Include orphan location strings — client still tagged even if location doc was removed.
    usedLocations.forEach((n) => { if (!seen.has(n)) { list.push({ id: `orphan-${n}`, name: n, orphan: true }); seen.add(n); } });
    return list;
  }, [locations, usedLocations]);
  const untaggedCount = useMemo(
    () => (clients || []).filter((c) => !c.default_location).length,
    [clients]
  );
  if (chips.length < 2 && usedLocations.size === 0) return null;
  const countFor = (name) => (clients || []).filter((c) => c.default_location === name).length;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="clients-location-filter-chips">
      <span className="tracker text-[10px] text-zinc-600 mr-1">[ LOCATION ]</span>
      <LocationChip v="all" label="All" count={clients.length} active={value === "all"} testid="client-loc-chip-all" onClick={onChange} />
      {chips.map((c) => (
        <LocationChip
          key={c.id}
          v={c.name}
          label={c.orphan ? `${c.name} (removed)` : c.name}
          count={countFor(c.name)}
          active={value === c.name}
          testid={`client-loc-chip-${c.name}`}
          onClick={onChange}
        />
      ))}
      {untaggedCount > 0 && (
        <LocationChip v="__none" label="No default" count={untaggedCount} active={value === "__none"} testid="client-loc-chip-none" onClick={onChange} />
      )}
    </div>
  );
}


